import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from "@angular/core";
import {forkJoin, Subject} from "rxjs";
import {Device, Site} from "../../../../domain/cloud-topology";
import {faPlusCircle, faTimesCircle} from "@fortawesome/free-solid-svg-icons";
import {SiteDeviceService} from "../../../../services/site/site-device.service";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {ToastService} from "../../../../services/toast-service";
import {SOLAR} from "../../../../domain/constants";
import {MatSnackBar} from "@angular/material/snack-bar";

@Component({
    selector: 'app-site-solar',
    templateUrl: './site-solar.component.html',
    styleUrls: ['./site-solar.component.scss'],
    standalone: false
})
export class SiteSolarComponent implements OnChanges, OnDestroy {

  @Input() site: Site;
  @Output() formDirty: EventEmitter<boolean> = new EventEmitter<boolean>();
  private subject$: Subject<void>;
  devices: FormArray;
  parentForm: FormGroup;
  faPlusCircle = faPlusCircle;
  faTimesCircle = faTimesCircle;
  solarDevices;
  constructor(private siteDeviceService: SiteDeviceService,
              private snackBar: MatSnackBar) { }

  ngOnChanges(): void {
    this.subject$ = new Subject<void>();
    this.setData();
  }

  private setData() {
    this.solarDevices = [];
    this.devices = new FormArray([]);
    if (this.site) {
      this.siteDeviceService.getDevices(this.site.id).subscribe(devices => {
        if (devices) {
          for (const device of devices) {
            if (device.config.type == SOLAR) {
              this.solarDevices.push(device);
            }
          }

          for (const solarDevice of this.solarDevices) {
            const device = new FormGroup({
              id: new FormControl(solarDevice.store.id),
              deviceName: new FormControl(solarDevice.store.name),
              nameplatePower: new FormControl(solarDevice.config['nameplateEnergy'])
            });
            this.devices.push(device);
          }
        } else {
          this.addBlankDevice();
        }
        this.parentForm = new FormGroup({
          devices: this.devices
        });
      });
    }
  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  addBlankDevice(): void {
    const blankDevice =
      new FormGroup({
        id: new FormControl(''),
        deviceName: new FormControl('New Solar Device', [Validators.required, Validators.min(1)]),
        nameplatePower: new FormControl(0, Validators.required),
      });

    this.solarDevices.push({
      store: { name: 'New Solar Device'},
      config: {
        'nameplate-energy': 0,
      }
    });
    this.devices.push(blankDevice);
  }

  save(): void {
    if(this.site) {
      if(window.confirm(`Are you sure you want to save solar devices for ${this.site.name}?`)){
        this.translateDeviceForm();
        const currentDevices = [];
        const newDevices = [];
        for (const solarDevice of this.solarDevices) {
          if (solarDevice.store.id != undefined) {
            currentDevices.push(solarDevice);
          } else {
            newDevices.push(solarDevice);
          }
        }

        let subscriptions = [];
        if (currentDevices.length) {
          subscriptions.push(this.siteDeviceService.putDevices(this.site.id, currentDevices));
        }

        if (newDevices.length) {
          subscriptions.push(this.siteDeviceService.postDevices(this.site.id, newDevices));
        }

        if (subscriptions.length) {
          forkJoin(subscriptions).subscribe(() => {
            this.formDirty.emit(false);
            this.setData();
            this.snackBar.open(`Solar devices have been saved for ${this.site.name}.`, "Close",{
              duration: 3000,
              horizontalPosition: "right",
              verticalPosition: "bottom"
            });
          }, (error) => {
            console.log(`Problem saving solar devices reason: ${error}`);
            this.snackBar.open(`Problem saving solar devices for ${this.site.name}.`, "Close",{
              duration: 3000,
              horizontalPosition: "right",
              verticalPosition: "bottom"
            });
          });
        }
      }
    }
  }

  cancel(): void {
    this.parentForm.reset();
    this.formDirty.emit(false);
    this.setData();
  }

  removeDevice(device): void {
    if (window.confirm(`Are you sure you want to remove this device?`)) {
      for (const deviceIdx in this.solarDevices) {
        if (deviceIdx == device) {
          if (this.solarDevices[deviceIdx].store.id == undefined) {
            this.solarDevices.splice(deviceIdx,1);
            this.devices.removeAt(device);
            this.snackBar.open(`Device has been deleted`, "Close",{
              duration: 3000,
              horizontalPosition: "right",
              verticalPosition: "bottom"
            });
            break;
          } else {
            this.siteDeviceService.deleteDevice(this.solarDevices[deviceIdx].store.id).subscribe(response => {
              this.snackBar.open(`Solar device has been deleted`, "Close",{
                duration: 3000,
                horizontalPosition: "right",
                verticalPosition: "bottom"
              });
              this.devices.removeAt(device);
              this.solarDevices.splice(deviceIdx, 1);
              this.formDirty.emit(false);
            }, (err) => {
              this.snackBar.open(`Problem deleting device`, "Close",{
                duration: 3000,
                horizontalPosition: "right",
                verticalPosition: "bottom"
              });
              console.error(`Problem deleting device reason: ${err}`);
            });
          }
        }
      }
    }
  }

  setDirtyForm(): void {
    this.formDirty.emit(true)
  }

  private translateDeviceForm(): void {
    let solarDevices: Device[] = [];
    for (const deviceForm of this.devices.value) {
      let solarDevice = {
        store: {
          name: deviceForm.deviceName,
        },
        config: {
          type: SOLAR,
          'nameplate-energy': deviceForm.nameplatePower,
        }
      };

      if (deviceForm.id != '') {
        solarDevice.store['id'] = deviceForm.id;
        solarDevice.store['siteId'] = this.site.id;
      }

      solarDevices.push(solarDevice);
    }

    this.solarDevices = solarDevices;
  }
}
